import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { getOrCreateConversation, sendMessage } from "@/lib/messaging";
import { MessageSquare, Send } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface MessageButtonProps {
	supplierId: string;
	supplierName: string;
}

export function MessageButton({
	supplierId,
	supplierName,
}: MessageButtonProps) {
	const { user, isSupplier } = useAuth();
	const [isOpen, setIsOpen] = useState(false);
	const [message, setMessage] = useState("");
	const [sending, setSending] = useState(false);
	const navigate = useNavigate();

	const handleSendMessage = async () => {
		if (!user || !message.trim()) return;

		try {
			setSending(true);

			// Get or create a conversation
			const conversationId = await getOrCreateConversation(
				user.id,
				supplierId
			);

			if (!conversationId) {
				throw new Error("Could not create conversation");
			}

			// Send the message
			const success = await sendMessage(
				user.id,
				supplierId,
				message.trim(),
				conversationId
			);

			if (success) {
				toast({
					title: "Message sent",
					description: `Your message has been sent to ${supplierName}`,
				});

				setIsOpen(false);
				setMessage("");

				// Navigate to the messages page
				navigate("/dashboard/messages");
			}
		} catch (error) {
			console.error("Error sending message:", error);
			toast({
				title: "Error",
				description: "Could not send message. Please try again.",
				variant: "destructive",
			});
		} finally {
			setSending(false);
		}
	};

	// Only retailers can message suppliers
	if (!user || isSupplier) {
		return null;
	}

	return (
		<>
			<Button
				variant="outline"
				onClick={() => setIsOpen(true)}
				className="gap-2">
				<MessageSquare className="h-4 w-4" />
				Message Supplier
			</Button>

			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							Send message to {supplierName}
						</DialogTitle>
					</DialogHeader>

					<div className="py-4">
						<Textarea
							placeholder={`What would you like to ask ${supplierName}?`}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							className="min-h-[120px]"
						/>
					</div>

					<DialogFooter className="sm:justify-between">
						<Button
							type="button"
							variant="outline"
							onClick={() => setIsOpen(false)}>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={handleSendMessage}
							disabled={!message.trim() || sending}
							className="gap-2">
							{sending ? (
								<span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
							) : (
								<Send className="h-4 w-4" />
							)}
							Send Message
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
